import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { useDisabledDatePresets } from '@/App/tiles/shared/useDisabledDatePresets.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { selectOptionLegsProperties, selectOptionLegsValidation } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';

type ExpiryProps = {
  legId: LegId;
  className?: string;
};

export function Expiry({ legId, className }: ExpiryProps) {
  const [checkOption] = useCheckOption();

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { expiryDate, expiryDateTenor, currencyPair, tileId, validation } = useOptionSelector((option) => {
    const legProperties = selectOptionLegsProperties(option, legId);
    const validation = selectOptionLegsValidation(option, legId)?.expiryDate;
    const tileId = option.properties.tileId;

    return {
      tileId,
      expiryDate: legProperties?.expiryDate,
      expiryDateTenor: legProperties?.expiryDateTenor,
      currencyPair: option.properties.currencyPair,
      validation,
    };
  });

  const pickerDisabledPresets = useDisabledDatePresets(tileId, currencyPair);

  function handleExpiryDateChange(dateOrTenor: string) {
    sgwtWebAnalytics?.trackEvent('Option', 'Expiry date selected', dateOrTenor);

    checkOption({
      tileId,
      legProperties: {
        legId,
        expiryDate: dateOrTenor,
      },
    });
  }

  return (
    <TradeCaptureValidationWrapper validation={validation}>
      <DatePickerWithTenorNext
        className={className}
        data-e2e="expiry-date"
        date={expiryDate ?? ''}
        tenor={expiryDateTenor}
        onChange={handleExpiryDateChange}
        disablePresets={pickerDisabledPresets}
      />
    </TradeCaptureValidationWrapper>
  );
}
